import React, { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import DashboardLayout from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { invoiceAPI } from '@/utils/api';
import { formatCurrency, formatDate, formatMonth } from '@/utils/formatters';
import { Receipt, Download, FileText } from 'lucide-react';
import { toast } from 'sonner';

const TranslatorInvoices = () => {
  const { user } = useOutletContext();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    loadInvoices();
  }, []);
  
  const loadInvoices = async () => {
    try {
      const response = await invoiceAPI.list({});
      setInvoices(response.data);
    } catch (error) {
      console.error('Error loading invoices:', error);
      toast.error('Failed to load invoices');
    } finally {
      setLoading(false);
    }
  };
  
  const handleDownloadPDF = (invoiceId, invoiceNumber) => {
    window.open(invoiceAPI.downloadPDF(invoiceId), '_blank');
  };
  
  const getStatusColor = (status) => {
    const colors = {
      generated: 'bg-creative/10 text-creative',
      sent: 'bg-warning/10 text-warning',
      paid: 'bg-success/10 text-success',
    };
    return colors[status] || 'bg-secondary text-secondary-foreground';
  };
  
  if (loading) {
    return (
      <DashboardLayout user={user}>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <div className="w-12 h-12 border-4 border-accent border-t-transparent rounded-full animate-spin mx-auto mb-4" />
            <p className="text-muted-foreground">Loading invoices...</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }
  
  return (
    <DashboardLayout user={user}>
      <div className="space-y-6">
        <div>
          <h1 className="text-4xl font-heading font-bold text-primary mb-2">My Invoices</h1>
          <p className="text-muted-foreground">View and download your monthly invoices</p>
        </div>
        
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Receipt className="w-5 h-5 text-accent" />
              All Invoices ({invoices.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {invoices.length === 0 ? (
              <div className="text-center py-12">
                <div 
                  className="w-32 h-32 mx-auto mb-6 rounded-xl bg-cover bg-center"
                  style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1758873269317-51888e824b28?crop=entropy&cs=srgb&fm=jpg&q=85)' }}
                />
                <h3 className="text-lg font-semibold text-primary mb-2">No invoices yet</h3>
                <p className="text-muted-foreground">Your monthly invoices will appear here once generated</p>
              </div>
            ) : (
              <div className="space-y-4">
                {invoices.map((invoice) => (
                  <Card 
                    key={invoice.invoice_id}
                    className="hover:shadow-md transition-shadow"
                    data-testid={`invoice-${invoice.invoice_id}`}
                  >
                    <CardContent className="pt-6">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-3">
                            <FileText className="w-6 h-6 text-accent" />
                            <div>
                              <h3 className="text-xl font-heading font-bold text-primary">{invoice.invoice_number}</h3>
                              <p className="text-sm text-muted-foreground">Period: {formatMonth(invoice.month)}</p>
                            </div>
                          </div>
                          
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                            <div>
                              <p className="text-xs text-muted-foreground mb-1">Subtotal</p>
                              <p className="text-lg font-semibold text-primary">{formatCurrency(invoice.subtotal)}</p>
                            </div>
                            
                            {invoice.tds_amount > 0 && (
                              <div>
                                <p className="text-xs text-muted-foreground mb-1">TDS (1%)</p>
                                <p className="text-lg font-semibold text-warning">-{formatCurrency(invoice.tds_amount)}</p>
                              </div>
                            )}
                            
                            <div>
                              <p className="text-xs text-muted-foreground mb-1">Net Payable</p>
                              <p className="text-lg font-bold text-success">{formatCurrency(invoice.net_payable)}</p>
                            </div>
                            
                            <div>
                              <p className="text-xs text-muted-foreground mb-1">Status</p>
                              <Badge className={getStatusColor(invoice.status)}>
                                {invoice.status}
                              </Badge>
                            </div>
                          </div>
                          
                          <div className="text-sm text-muted-foreground space-y-1">
                            <p>Invoice Date: {formatDate(invoice.invoice_date)}</p>
                            {invoice.payment_date && (
                              <p className="text-success">Paid on: {formatDate(invoice.payment_date)}</p>
                            )}
                            {invoice.payment_reference && (
                              <p>Payment Ref: {invoice.payment_reference}</p>
                            )}
                          </div>
                        </div>
                        
                        <Button
                          onClick={() => handleDownloadPDF(invoice.invoice_id, invoice.invoice_number)}
                          variant="outline"
                          size="sm"
                          data-testid={`btn-download-${invoice.invoice_id}`}
                        >
                          <Download className="w-4 h-4 mr-2" />
                          Download PDF
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default TranslatorInvoices;
